// Medication windows (build plan §9.1 #5; Decisions 2 #18): staff-set codes,
// names and clock times in Config Windows, read by everything that lists
// windows (confirm, the pass, missed meds, print, reports). A window is never
// deleted: a retired one keeps its code and name so the history that names
// it still reads, and is no longer offered for new orders or passes.
//
// Times are Eastern clock times ("HH:MM", 24-hour), as Config holds them.
// Pure, and free of server-only imports (the SPA imports it).

export interface WindowDef {
  code: string;
  name: string;
  startTime: string;
  endTime: string;
  /** Lower first; blank sorts after numbered ones, by start time. */
  sortOrder: number | null;
  retired: boolean;
}

const s = (v: unknown): string => (v === undefined || v === null ? "" : String(v)).trim();

/** Every window in Config (retired ones too), in display order. */
export function windowsOf(v: unknown): WindowDef[] {
  if (!Array.isArray(v)) return [];
  return v
    .map((w: Record<string, unknown>) => {
      const n = typeof w?.sortOrder === "number" ? w.sortOrder : Number.parseInt(s(w?.sortOrder), 10);
      return { code: s(w?.code), name: s(w?.name), startTime: s(w?.startTime), endTime: s(w?.endTime), sortOrder: Number.isFinite(n) ? n : null, retired: s(w?.retired) === "Y" || w?.retired === true };
    })
    .filter((w) => w.code)
    .sort((a, b) => (a.sortOrder ?? 1e6) - (b.sortOrder ?? 1e6) || a.startTime.localeCompare(b.startTime) || a.code.localeCompare(b.code));
}

/** The windows offered for new orders and passes. */
export function activeWindows(v: unknown): WindowDef[] {
  return windowsOf(v).filter((w) => !w.retired);
}

/** "Morning (07:00–08:30)", or the bare code for one Config no longer lists. */
export function windowLabel(all: WindowDef[], code: string): string {
  const w = all.find((x) => x.code === code);
  return w ? `${w.name} (${w.startTime}–${w.endTime})` : code;
}

const minutes = (hhmm: string): number => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm);
  return m ? Number(m[1]) * 60 + Number(m[2]) : Number.NaN;
};

/** Two windows overlap when each starts before the other ends (touching ends do not overlap). */
export function overlaps(a: Pick<WindowDef, "startTime" | "endTime">, b: Pick<WindowDef, "startTime" | "endTime">): boolean {
  return minutes(a.startTime) < minutes(b.endTime) && minutes(b.startTime) < minutes(a.endTime);
}

/** The first pair of active windows that overlap, or null. */
export function firstOverlap(ws: Pick<WindowDef, "code" | "startTime" | "endTime" | "retired">[]): [string, string] | null {
  const live = ws.filter((w) => !w.retired);
  for (let i = 0; i < live.length; i++) for (let j = i + 1; j < live.length; j++) if (overlaps(live[i], live[j])) return [live[i].code, live[j].code];
  return null;
}

/** Minutes since midnight in New York for an instant. */
export function easternMinutes(ms: number): number {
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(new Date(ms))
      .map((x) => [x.type, x.value])
  );
  return (Number(p.hour) % 24) * 60 + Number(p.minute);
}

export type WindowPhase = "before" | "open" | "closed";

/** Where the clock is relative to a window on its own day. */
export function windowPhase(w: Pick<WindowDef, "startTime" | "endTime">, nowMinutes: number): WindowPhase {
  if (nowMinutes < minutes(w.startTime)) return "before";
  if (nowMinutes < minutes(w.endTime)) return "open";
  return "closed";
}

/**
 * The pass's default window: the one open now, else the next to open today,
 * else the last of the day (the evening pass after hours). Null when none.
 */
export function currentOrNextWindow(ws: WindowDef[], nowMs: number): WindowDef | null {
  const live = ws.filter((w) => !w.retired).sort((a, b) => a.startTime.localeCompare(b.startTime));
  if (!live.length) return null;
  const m = easternMinutes(nowMs);
  return live.find((w) => windowPhase(w, m) === "open") ?? live.find((w) => windowPhase(w, m) === "before") ?? live[live.length - 1];
}

/**
 * The windows a parent's "times of day" point to, by clock: morning starts
 * before 11:00, noon 11:00–14:59, evening 15:00 or later (the first such
 * window each). "As needed" and "other" point to none: those are set by
 * hand (M3 Stage 3 fix 5).
 */
export function windowsForTimesOfDay(times: readonly string[] | undefined, ws: WindowDef[]): string[] {
  const live = ws.filter((w) => !w.retired).sort((a, b) => a.startTime.localeCompare(b.startTime));
  const pick = (lo: number, hi: number) => live.find((w) => minutes(w.startTime) >= lo && minutes(w.startTime) < hi)?.code;
  const out: string[] = [];
  for (const t of times ?? []) {
    const code = t === "morning" ? pick(0, 11 * 60) : t === "noon" ? pick(11 * 60, 15 * 60) : t === "evening" ? pick(15 * 60, 24 * 60) : undefined;
    if (code && !out.includes(code)) out.push(code);
  }
  return out;
}

/** The instant a New York clock time falls on a given day ("2027-06-21", "07:00"), daylight saving included. */
export function easternToUtcMs(day: string, hhmm: string): number {
  const [y, mo, d] = day.split("-").map(Number);
  const want = minutes(hhmm);
  for (const offsetH of [4, 5]) {
    const ms = Date.UTC(y, mo - 1, d, 0, 0) + want * 60_000 + offsetH * 3_600_000;
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(new Date(ms)).map((x) => [x.type, x.value]));
    if (`${p.year}-${p.month}-${p.day}` === day && easternMinutes(ms) === want) return ms;
  }
  return Date.UTC(y, mo - 1, d, 0, 0) + want * 60_000 + 5 * 3_600_000;
}
